import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Stack,
} from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import SettingsIcon from '@mui/icons-material/Settings'
import ImageIcon from '@mui/icons-material/Image'
import UploadIcon from '@mui/icons-material/Upload'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from '../../config/firebase'
import { useResourceStore } from '../../stores/resourceStore'
import { Resource } from '../../models/Resource'

const resourceSchema = z.object({
  title: z.string().min(1, '請輸入標題'),
  type: z.enum(['community', 'website', 'youtube']),
  url: z.string().url('請輸入有效的網址'),
  description: z.string().default(''),
  imageUrl: z.string().optional().default(''),
  tags: z.string(),
  jobCategory: z.string().optional(),
  order: z.number().min(0),
  isActive: z.boolean(),
})

type ResourceForm = z.infer<typeof resourceSchema>

const tagTabs = [
  { value: 'all', label: '全部' },
  { value: '說明頁', label: '說明頁' },
  { value: '職業願景', label: '場域專業深度剖析' },
  { value: '國外經驗分享', label: '國外經驗分享' },
]

const DEFAULT_JOB_CATEGORIES = [
  '手術室',
  '精神科病房',
  '加護病房',
  '專科護理師',
  '護理主管',
  '護理教師',
  '開刀房刀住',
  '急診',
  '麻護',
]

const JOB_CATEGORIES_DOC = 'settings/jobCategories'

export default function ResourceListPage() {
  const { resources, loading, error, fetchResources, addResource, updateResource, deleteResource } =
    useResourceStore()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingResource, setEditingResource] = useState<Resource | null>(null)
  const [imageUploading, setImageUploading] = useState(false)
  const [tagFilter, setTagFilter] = useState('all')

  // 動態職務分類
  const [jobCategories, setJobCategories] = useState<string[]>(DEFAULT_JOB_CATEGORIES)
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [newCategory, setNewCategory] = useState('')

  // 擷取縮圖
  const [fetchingOgId, setFetchingOgId] = useState<string | null>(null)
  const [fetchingOg, setFetchingOg] = useState(false)

  // 說明頁設定
  const INFO_PAGE_DOC = 'settings/infoPage'
  const DEFAULT_INFO_DESCRIPTION =
    '「NurseBridge」APP 專為護理學生設計，提供量身打造的心理健康評估、同儕支持與資源，關於 APP 的使用介紹，可以先透過影片概略瞭解後再自行探索。'
  const [infoPageDialogOpen, setInfoPageDialogOpen] = useState(false)
  const [infoDescription, setInfoDescription] = useState(DEFAULT_INFO_DESCRIPTION)

  const fetchInfoPageSettings = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, INFO_PAGE_DOC))
      if (snap.exists()) {
        const data = snap.data()
        if (typeof data.description === 'string' && data.description.length > 0) {
          setInfoDescription(data.description)
        }
      }
    } catch {
      // 讀取失敗時使用預設值
    }
  }, [])

  const saveInfoPageSettings = async () => {
    await setDoc(doc(db, INFO_PAGE_DOC), { description: infoDescription })
    setInfoPageDialogOpen(false)
  }

  const fetchJobCategories = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, JOB_CATEGORIES_DOC))
      if (snap.exists()) {
        const data = snap.data()
        if (Array.isArray(data.categories) && data.categories.length > 0) {
          setJobCategories(data.categories)
        }
      }
    } catch {
      // 讀取失敗時使用預設值
    }
  }, [])

  const saveJobCategories = async (categories: string[]) => {
    await setDoc(doc(db, JOB_CATEGORIES_DOC), { categories })
    setJobCategories(categories)
  }

  const handleAddCategory = async () => {
    const trimmed = newCategory.trim()
    if (!trimmed || jobCategories.includes(trimmed)) return
    await saveJobCategories([...jobCategories, trimmed])
    setNewCategory('')
  }

  const handleRemoveCategory = async (cat: string) => {
    await saveJobCategories(jobCategories.filter((c) => c !== cat))
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ResourceForm>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      type: 'website',
      order: 0,
      isActive: true,
    },
  })

  const watchedTags = watch('tags')
  const isCareerVision = watchedTags?.includes('職業願景')

  useEffect(() => {
    fetchResources()
    fetchJobCategories()
    fetchInfoPageSettings()
  }, [fetchResources, fetchJobCategories, fetchInfoPageSettings])

  // 前端篩選
  const filteredResources = useMemo(() => {
    if (tagFilter === 'all') return resources
    return resources.filter((r) => r.tags.includes(tagFilter))
  }, [resources, tagFilter])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTagFilter(tagTabs[newValue].value)
  }

  const currentTabIndex = tagTabs.findIndex((t) => t.value === tagFilter)

  const handleOpenDialog = (resource?: Resource) => {
    if (resource) {
      setEditingResource(resource)
      // 找出職務分類（tags 中非「職業願景」的部分）
      const jobCat = resource.tags.find((t) => jobCategories.includes(t)) || ''
      reset({
        title: resource.title,
        type: resource.type,
        url: resource.url,
        description: resource.description,
        imageUrl: resource.imageUrl || '',
        tags: resource.tags.join(', '),
        jobCategory: jobCat,
        order: resource.order,
        isActive: resource.isActive,
      })
    } else {
      setEditingResource(null)
      // 根據目前篩選 tab 預填
      const isCareerTab = tagFilter === '職業願景'
      reset({
        title: '',
        type: isCareerTab ? 'youtube' : 'website',
        url: '',
        description: '',
        imageUrl: '',
        tags: tagFilter !== 'all' ? tagFilter : '',
        jobCategory: '',
        order: filteredResources.length,
        isActive: true,
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingResource(null)
    reset()
  }

  const onSubmit = async (data: ResourceForm) => {
    let tags = data.tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t)

    // 如果有選職務分類且 tags 包含「職業願景」，確保分類也在 tags 裡
    if (data.jobCategory && tags.includes('職業願景')) {
      // 移除舊的職務分類 tag，加入新的
      tags = tags.filter((t) => !jobCategories.includes(t))
      tags.push(data.jobCategory)
    }

    try {
      if (editingResource) {
        await updateResource(editingResource.id, {
          ...data,
          tags,
        })
      } else {
        await addResource({
          ...data,
          tags,
        })
      }
      handleCloseDialog()
    } catch {
      // Error handled by store
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除此資源嗎？')) {
      await deleteResource(id)
    }
  }

  const handleFetchOgImageForRow = async (id: string, url: string) => {
    if (!url) return
    setFetchingOgId(id)
    try {
      const fetchOgImageFn = httpsCallable<{ url: string }, { imageUrl: string | null }>(
        functions,
        'fetchOgImage'
      )
      const result = await fetchOgImageFn({ url })
      if (result.data.imageUrl) {
        await updateResource(id, { imageUrl: result.data.imageUrl })
      }
    } catch (err) {
      console.error('OG image fetch failed:', err)
    } finally {
      setFetchingOgId(null)
    }
  }

  const handleFetchOgImage = async () => {
    const url = watch('url')
    if (!url) return
    setFetchingOg(true)
    try {
      const fetchOgImageFn = httpsCallable<{ url: string }, { imageUrl: string | null }>(
        functions,
        'fetchOgImage'
      )
      const result = await fetchOgImageFn({ url })
      if (result.data.imageUrl) {
        reset({ ...watch(), imageUrl: result.data.imageUrl })
      }
    } catch (err) {
      console.error('OG image fetch failed:', err)
    } finally {
      setFetchingOg(false)
    }
  }

  const columns: GridColDef[] = [
    { field: 'order', headerName: '順序', width: 70, type: 'number' },
    { field: 'title', headerName: '標題', width: 200 },
    {
      field: 'imageUrl',
      headerName: '縮圖',
      width: 80,
      renderCell: (params) =>
        params.value ? (
          <Box
            component="img"
            src={params.value}
            sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
          />
        ) : (
          <ImageIcon color="disabled" />
        ),
    },
    {
      field: 'type',
      headerName: '類型',
      width: 100,
      renderCell: (params) => {
        const typeMap: Record<string, { label: string; color: 'success' | 'info' | 'error' }> = {
          community: { label: '社群', color: 'success' },
          website: { label: '網站', color: 'info' },
          youtube: { label: 'YouTube', color: 'error' },
        }
        const type = typeMap[params.value] || { label: params.value, color: 'info' as const }
        return <Chip label={type.label} color={type.color} size="small" />
      },
    },
    { field: 'description', headerName: '描述', width: 250 },
    {
      field: 'tags',
      headerName: '標籤',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(params.value as string[]).slice(0, 3).map((tag, i) => (
            <Chip key={i} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: '狀態',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? '啟用' : '停用'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            fetchingOgId === params.row.id ? (
              <CircularProgress size={18} />
            ) : (
              <RefreshIcon />
            )
          }
          label="擷取縮圖"
          onClick={() => handleFetchOgImageForRow(params.row.id, params.row.url)}
          disabled={fetchingOgId === params.row.id}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="編輯"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="刪除"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ]

  if (loading && resources.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          資源管理
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setInfoPageDialogOpen(true)}
          >
            說明頁設定
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setCategoryDialogOpen(true)}
          >
            職務分類管理
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            新增資源
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tag filter tabs */}
      <Tabs
        value={currentTabIndex >= 0 ? currentTabIndex : 0}
        onChange={handleTabChange}
        sx={{ mb: 2 }}
      >
        {tagTabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} />
        ))}
      </Tabs>

      <Card>
        <DataGrid
          rows={filteredResources}
          columns={columns}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
          }}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
          }}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingResource ? '編輯資源' : '新增資源'}
          </DialogTitle>
          <DialogContent>
            <TextField
              {...register('title')}
              label="標題"
              fullWidth
              margin="normal"
              error={!!errors.title}
              helperText={errors.title?.message}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>類型</InputLabel>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="類型">
                    <MenuItem value="community">社群資源</MenuItem>
                    <MenuItem value="website">網站</MenuItem>
                    <MenuItem value="youtube">YouTube</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <TextField
              {...register('url')}
              label="網址"
              fullWidth
              margin="normal"
              error={!!errors.url}
              helperText={errors.url?.message}
            />
            <TextField
              {...register('description')}
              label="描述（選填）"
              fullWidth
              margin="normal"
              multiline
              rows={3}
            />
            {/* 縮圖 */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                縮圖
              </Typography>
              {watch('imageUrl') && (
                <Box
                  component="img"
                  src={watch('imageUrl')}
                  sx={{
                    width: 120,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    mb: 1,
                    display: 'block',
                  }}
                />
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    fetchingOg ? <CircularProgress size={16} /> : <CloudDownloadIcon />
                  }
                  onClick={handleFetchOgImage}
                  disabled={fetchingOg || imageUploading || !watch('url')}
                >
                  從網址擷取
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  disabled={imageUploading}
                  component="label"
                >
                  {imageUploading ? '上傳中...' : '上傳圖片'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      setImageUploading(true)
                      try {
                        const fileRef = storageRef(
                          storage,
                          `resource_thumbnails/${Date.now()}_${file.name}`
                        )
                        await uploadBytes(fileRef, file)
                        const downloadUrl = await getDownloadURL(fileRef)
                        reset({ ...watch(), imageUrl: downloadUrl })
                      } catch (err) {
                        console.error('Upload failed:', err)
                      } finally {
                        setImageUploading(false)
                      }
                    }}
                  />
                </Button>
              </Stack>
              <TextField
                {...register('imageUrl')}
                label="圖片網址（選填）"
                fullWidth
                margin="dense"
                size="small"
                placeholder="或手動貼上圖片 URL"
              />
            </Box>

            <TextField
              {...register('tags')}
              label="標籤 (以逗號分隔)"
              fullWidth
              margin="normal"
              placeholder="心理健康, 壓力管理, 自我照顧"
            />

            {/* 職務分類（僅在 tags 含「職業願景」時顯示） */}
            {isCareerVision && (
              <FormControl fullWidth margin="normal">
                <InputLabel>職務分類</InputLabel>
                <Controller
                  name="jobCategory"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="職務分類">
                      <MenuItem value="">（不選擇）</MenuItem>
                      {jobCategories.map((cat) => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            )}

            <TextField
              {...register('order', { valueAsNumber: true })}
              label="排序"
              type="number"
              fullWidth
              margin="normal"
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="啟用"
                  sx={{ mt: 1 }}
                />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>取消</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : '儲存'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 說明頁設定 Dialog */}
      <Dialog
        open={infoPageDialogOpen}
        onClose={() => setInfoPageDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>說明頁設定</DialogTitle>
        <DialogContent>
          <TextField
            label="說明文字"
            fullWidth
            multiline
            rows={5}
            margin="normal"
            value={infoDescription}
            onChange={(e) => setInfoDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoPageDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={saveInfoPageSettings}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 職務分類管理 Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>職務分類管理</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
            分類格子在 App 的順序由「影片 order」自動決定（取該分類最小 order 影片）。此處只負責新增/刪除分類。
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {jobCategories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onDelete={() => handleRemoveCategory(cat)}
                deleteIcon={<CloseIcon />}
              />
            ))}
            {jobCategories.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                尚無分類，請新增
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="新增分類"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddCategory()
                }
              }}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleAddCategory}
              disabled={!newCategory.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              新增
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>關閉</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
